import capitalize from "lodash-es/capitalize";
import maxBy from "lodash-es/maxBy";

import {
  RunObservabilitySeverity,
  RunState,
  RunWorkerMetrics,
  RunWorkerMetricsState,
  RunWorkerOutcome,
  RunWorkerOutcomeCategory,
} from "types/generated";
import { getDuration } from "utils/time";

const MILLISECONDS_IN_SECOND = 1000;
const SECONDS_IN_MINUTE = 60;

const toOneDecimal = (value: number) => Math.round(value * 10) / 10;

export const getPhaseLabel = (state: RunState) => capitalize(state.replace(/_/g, " "));

export const getDurationLabel = (seconds: number) => {
  if (seconds < 1) {
    return `${Math.round(seconds * MILLISECONDS_IN_SECOND)}ms`;
  }

  const rounded = seconds < SECONDS_IN_MINUTE ? toOneDecimal(seconds) : Math.round(seconds);

  return rounded < SECONDS_IN_MINUTE ? `${rounded}s` : (getDuration(rounded, true, false) ?? "0s");
};

const isOverP95 = ({ duration: { p95Severity } }: RunWorkerMetricsState) =>
  !!p95Severity && p95Severity !== RunObservabilitySeverity.None;

export const getRunObservabilityAttentionState = (states: RunWorkerMetricsState[]) =>
  states.findLast(({ failed }) => failed) ??
  maxBy(states.filter(isOverP95), ({ duration }) => duration.p95Ratio);

const getRunObservabilityAttentionLabel = (
  { states, duration }: RunWorkerMetrics,
  outcome?: RunWorkerOutcome | null,
  runState?: RunState
) => {
  const state = getRunObservabilityAttentionState(states);

  if (outcome?.category === RunWorkerOutcomeCategory.Failed && !state?.failed) {
    return outcome.title;
  }

  if (state) {
    return `${getPhaseLabel(state.runState)} ${state.failed ? "failed" : "ran over its p95"}`;
  }

  if (
    outcome?.category === RunWorkerOutcomeCategory.Slow ||
    duration.p95Severity === RunObservabilitySeverity.Major
  ) {
    return "This run ran over its p95";
  }

  if (runState === RunState.Failed) {
    return "This run failed";
  }

  return undefined;
};

export const getRunObservabilityAttentionCaption = (
  workerMetrics: RunWorkerMetrics,
  outcome?: RunWorkerOutcome | null,
  runState?: RunState
) => {
  const label = getRunObservabilityAttentionLabel(workerMetrics, outcome, runState);

  if (!label) {
    return undefined;
  }

  const hasPhaseTimes = workerMetrics.states.some(({ duration }) => !!duration.seconds);
  const cards = hasPhaseTimes
    ? "Phase execution time and the slowest items cards"
    : "the slowest items cards";

  return `${label}. See ${cards} below.`;
};
